/**
 * What a search box asks for, once typing pauses (sapuan isian, the dropped-keystroke ruling).
 * Not `useDeferredValue`: its background render is a Transition, and inside the route's
 * <ViewTransition> React commits a Transition within the browser's view transition, a frame
 * late; a key typed in that frame was reset out of the field ("Petrus" became "Ptrus"). Here
 * the query moves by an ordinary update after `ms` without a key, so typing never waits on a
 * transition. An emptied field clears at once; a value there from the start (the history
 * entry's, `kueriAwal`) is asked at once. Pass the trimmed text. The caller still drops a stale
 * answer (its effect's `alive` flag), as before.
 */
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useSesi } from '../../app/store/sesi'

/** The pause in typing after which a search asks (ms). */
export const JEDA_KUERI_MS = 150

export function useKueriJeda(teks: string, ms: number = JEDA_KUERI_MS): string {
  const [kueri, setKueri] = useState(teks)
  useEffect(() => {
    if (teks === '') {
      setKueri('')
      return
    }
    const jam = setTimeout(() => setKueri(teks), ms)
    return () => clearTimeout(jam)
  }, [teks, ms])
  return teks === '' ? '' : kueri
}

/**
 * The query a history entry's state holds (`{ q, qUntuk }`) for `pengguna` (the session's
 * user id), or ''. A query another person typed on this device (their entry, still in the
 * tab's history: Back, a reload after the tab was taken over), or one from before the entry
 * said whose it is, is never theirs to see.
 */
export function kueriDariKeadaan(keadaan: unknown, pengguna: string): string {
  if (typeof keadaan !== 'object' || keadaan === null) return ''
  const { q, qUntuk } = keadaan as { q?: unknown; qUntuk?: unknown }
  return typeof q === 'string' && qUntuk === pengguna ? q : ''
}

/** Whether the entry's state holds a query `pengguna` may not see (another person's). */
function kueriAsing(keadaan: unknown, pengguna: string): boolean {
  if (typeof keadaan !== 'object' || keadaan === null) return false
  const k = keadaan as { q?: unknown; qUntuk?: unknown }
  return (k.q !== undefined || k.qUntuk !== undefined) && k.qUntuk !== pengguna
}

/**
 * The entry's state with `pengguna`'s query set (or dropped when empty), with whose it is;
 * any other person's query goes, the entry's other keys stay.
 */
export function denganKueri(
  keadaan: unknown,
  q: string,
  pengguna: string,
): Record<string, unknown> | null {
  const lain: Record<string, unknown> =
    typeof keadaan === 'object' && keadaan !== null
      ? { ...(keadaan as Record<string, unknown>) }
      : {}
  delete lain.q
  delete lain.qUntuk
  if (q !== '') {
    lain.q = q
    lain.qUntuk = pengguna
  }
  return Object.keys(lain).length === 0 ? null : lain
}

/**
 * The search a list (or the print sheet it opens) starts with for `pengguna`: its history
 * entry's (Back, a reload) when it is theirs, else an address from before this rule (`?q=`),
 * which `useKueriRiwayat` then takes out of the address.
 */
export function kueriAwal(lokasi: { state: unknown; search: string }, pengguna: string): string {
  const dariKeadaan = kueriDariKeadaan(lokasi.state, pengguna)
  if (dariKeadaan !== '') return dariKeadaan
  return new URLSearchParams(lokasi.search).get('q') ?? ''
}

/**
 * Keeps a list's committed query in its history entry's state, never in the address: the
 * person search reaches NIK and the family search both KK numbers, and a number typed there
 * must not reach a URL (browser history sync, the server's log of a reload; user decision
 * 2026-10-01). Back and a reload restore it from the entry (the sign-in lock hands the entry's
 * state back with its address, `auth/guard.tsx`). The entry says whose search it is (the
 * session's user): on a shared device another person's search, reached by Back or kept over a
 * reload, is never shown and is taken out of the entry. An address that still carries `?q=`
 * (a bookmark from before) loses it here, its query kept in the state. The entry is replaced
 * with `flushSync`, from a microtask: a navigation renders in a Transition, and keys typed
 * after the pause were lost the way `useKueriJeda` describes (React refuses a flushSync inside
 * an effect).
 */
export function useKueriRiwayat(q: string): void {
  const lokasi = useLocation()
  const navigate = useNavigate()
  const pengguna = useSesi((s) => s.sesi.userId)
  useEffect(() => {
    const params = new URLSearchParams(lokasi.search)
    const sama = kueriDariKeadaan(lokasi.state, pengguna) === q
    if (!params.has('q') && sama && !kueriAsing(lokasi.state, pengguna)) return
    let aktif = true
    queueMicrotask(() => {
      if (!aktif) return
      params.delete('q')
      const search = params.toString()
      void navigate(
        { pathname: lokasi.pathname, search: search === '' ? '' : `?${search}`, hash: lokasi.hash },
        { replace: true, state: denganKueri(lokasi.state, q, pengguna), flushSync: true },
      )
    })
    return () => {
      aktif = false
    }
  }, [q, lokasi, navigate, pengguna])
}
